import dayjs from 'dayjs';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { PropTypes } from 'prop-types';
// Removing imports for MVP0 - Story #417
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Tbody, Td, Th, Thead } from '@ge/components/table';
import styled from 'styled-components';
import { TooltipCell } from '@ge/components/table/table';

import { PanelColumnWidths, } from '@ge/models/constants';
import { getDuration, getDateTimeBasedOnZone } from '@ge/shared/util/time-date';
import { elevations } from '@ge/tokens/elevations';

const TurbineContent = styled.div`
  display: flex;
  font-family: GE Inspira;
  line-height: 1.5;
  flex-direction: column;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px 8px;
  margin: 10px 5px;
  min-height: 50px;
  box-shadow: 0px 2px 5px rgb(0 0 0 / 10%), 0px 0px 6px rgb(0 0 0 / 10%);
`;
const StyledTr = styled.tr`
  line-height: 16px;
  opacity: ${(props) => (props.isDisconnected ? 0.4 : 1)};
`;
const StyledTd = styled(Td)`
  padding: 4px 8px;
  font-size: 11px;
`;

const StyledCode = styled.span`
  display: inline-block;
  font-size: 12px;
  vertical-align: middle;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledDesc = styled.span`
  margin-left: 6px;
`;

const StyledTimeDate = styled.div`
  span {
    display: inline-block;

    &:first-of-type {
      margin-right: 10px;
    }

    &:last-of-type {
      color: ${(props) => props.theme.table.textLightColor};
    }
  }
`;

const currentTime = new Date().getTime();

const AssetActiveAlarmInfo = ({ asset, siteId }) => {
    const { t, ready } = useTranslation(['entity-details'], {
        useSuspense: false,
    });
    const { getSiteById } = useStoreState((store) => store.sites);
    const site = getSiteById(siteId);

    const { alarms } = asset || {};

    if (!ready) {
        return null;
    }
    return (
        <TurbineContent>
            <Table height={'400'} compressed>
                <Thead transparent noTitles>
                    <StyledTr>
                        <Th
                            labels={{
                                [t('alarms_table.code', 'Code / #')]: 'code',
                            }}
                            align="left"
                            width={PanelColumnWidths.XXSMALL}
                        />
                        <Th
                            labels={{
                                [t('alarms_table.title', 'Title')]: 'description',
                            }}
                            align="left"
                            width={PanelColumnWidths.LARGE}
                            maxWidth={PanelColumnWidths.MEDIUM}
                        />
                        <Th
                            labels={{
                                [t('alarms_table.duration', 'Duration')]: 'duration',
                            }}
                            align="left"
                            width={PanelColumnWidths.SMALL}
                        />
                        <Th
                            labels={{
                                [`${t('alarms_table.start', 'Start')} (${dayjs
                                    .tz(new Date(), site?.timezone)
                                    .format('z')})`]: 'start',
                            }}
                            align="left"
                            width={PanelColumnWidths.LARGE}
                        />
                    </StyledTr>
                </Thead>
                <Tbody transparent>
                    {alarms?.map((alarm, idx) => {

                        const alarmDateTime =
                            alarm.start !== 'Invalid Date'
                                ? getDateTimeBasedOnZone(alarm.start, site?.timezone)
                                : null;

                        return (
                            <StyledTr key={`${idx}-${alarm.code}`} >
                                <StyledTd border align="left" width={PanelColumnWidths.XXSMALL}>
                                    <TooltipCell tooltip={alarm.code} zIndex={elevations.P20}>
                                        <StyledCode>{alarm.code}</StyledCode>
                                    </TooltipCell>
                                </StyledTd>
                                <StyledTd border align="left" width={PanelColumnWidths.LARGE}
                                    maxWidth={PanelColumnWidths.MEDIUM}>
                                    <TooltipCell tooltip={alarm.description} zIndex={elevations.P20}>
                                        <StyledDesc>{alarm.description}</StyledDesc>
                                    </TooltipCell>
                                </StyledTd>
                                <StyledTd border align="left" width={PanelColumnWidths.XSMALL}
                                >
                                    {getDuration(alarm.start, dayjs(currentTime)).formatted}
                                </StyledTd>
                                <StyledTd border align="left" width={PanelColumnWidths.LARGE}>
                                    {alarmDateTime && (
                                        <StyledTimeDate>
                                            <span>{alarmDateTime.timeInSecs}</span>
                                            <span>{alarmDateTime.date}</span>
                                        </StyledTimeDate>
                                    )}
                                </StyledTd>
                            </StyledTr>
                        );
                    })}
                </Tbody>
            </Table>
        </TurbineContent>
    );
};

AssetActiveAlarmInfo.propTypes = {
    asset: PropTypes.shape({
        metrics: PropTypes.object,
    }).isRequired,
    siteId: PropTypes.string,
};

export default AssetActiveAlarmInfo;
